// The public site's ad and visit measurement: Google's tag, the visitor's choice
// about it, and the ad click a visit came from.
//
// THE RULE (`consentDefault: denied`, the only value the server answers):
// nothing of Google's loads, and nothing is stored or sent about a visit, until
// the visitor allows it - in every country. The contract keeps `granted` as a
// value a client must understand, and that is a different contract: the tag
// then loads on every page for every visitor at once, starting allowed outside
// the EEA, the UK and Switzerland and refused inside them - where Google still
// receives cookieless pings before any answer (Consent Mode's "advanced" form),
// and where the bar's question is no longer true outside them. Which tag, if
// any, the operator sets in the console; the page reads it from the api
// (GET /site-measurement), so the documents stay static, and with none set the
// site loads no tag and shows the plain cookie notice.
//
// Three things are kept on the device, each only after the visitor allowed it
// (or, for the choice itself, chose):
//   - the choice, in local storage, for a year or until CONSENT_VERSION moves;
//   - the ad click the visit came from (`gclid` & co. and the `utm_*` words), in
//     a first-party cookie for ATTRIBUTION_DAYS, so a request sent a week after
//     the click still says which ad brought it;
//   - whatever Google's own tag keeps under its own names.
// A refusal after an allowance takes the ad click away and Google's cookies and
// storage on this site with it - on this page at once, and on any other page of
// the site open in another tab or kept by Back (Measurement.sync) - and the page
// it was given on tells the api which clicks it kept, so a request sent under the
// allowance loses its click too and nothing about it is reported again
// (POST /site-measurement/withdrawals). Measurement
// switched off takes all three away on the next page the visitor opens, and an
// answer that lapses takes Google's (Measurement.begin): the notices say nothing
// of Google's is on the device while nothing of Google's runs. Google's
// cookies are set on this host alone (`cookie_domain: 'none'`), never on the
// whole domain, so the panels and the schools' sites under it never receive them.
//
// The conversion is a RECORDED demo request: it is reported from the form's
// success handler, with the id the server answered as its transaction id, so a
// click, a refused request, a reload or Back reports nothing, and Google counts
// one request once however often a tag fires for it.
//
// The pure half (shapes, parsing, the commands) is unit-tested in
// measure.test.ts; the page half takes the document and window it acts on.

export type Choice = 'granted' | 'denied';
export type Audience = 'teacher' | 'school';

/** What GET /site-measurement says, held to the shapes the server holds it to. */
export interface SiteSettings {
  adsId?: string;
  /** The conversion a recorded request counts as, by who asked: one label per answer to "who are you". */
  adsTeacherLabel?: string;
  adsSchoolLabel?: string;
  ga4Id?: string;
  consentDefault: Choice;
}

const ADS_ID = /^AW-[0-9]{5,15}$/;
const ADS_LABEL = /^[A-Za-z0-9_-]{4,64}$/;
const GA4_ID = /^G-[A-Z0-9]{4,20}$/;

/**
 * Reads the api's answer. Null when it names no usable id - the site then
 * measures nothing. The network is not trusted to keep the shapes: an id that is
 * not one is dropped here too, because it ends up in a script URL.
 */
export function readSettings(body: unknown): SiteSettings | null {
  if (typeof body !== 'object' || body === null) return null;
  const b = body as Record<string, unknown>;
  const pick = (v: unknown, shape: RegExp) => (typeof v === 'string' && shape.test(v) ? v : undefined);
  const adsId = pick(b.googleAdsId, ADS_ID);
  const ga4Id = pick(b.ga4MeasurementId, GA4_ID);
  if (!adsId && !ga4Id) return null;
  return {
    adsId,
    adsTeacherLabel: adsId ? pick(b.googleAdsTeacherLabel, ADS_LABEL) : undefined,
    adsSchoolLabel: adsId ? pick(b.googleAdsSchoolLabel, ADS_LABEL) : undefined,
    ga4Id,
    consentDefault: b.consentDefault === 'granted' ? 'granted' : 'denied',
  };
}

// --- The visitor's choice -----------------------------------------------------

export const CONSENT_KEY = 'etc-consent';
/**
 * Bump when what the visitor is asked to allow changes: every stored answer then
 * lapses and the bar asks again, and a request records which version it was
 * allowed under. 2: the question names the requests and accounts an ad leads to,
 * which the registrations file reports to Google Ads.
 */
export const CONSENT_VERSION = 2;
/** An answer is asked again after a year. */
export const CONSENT_MAX_AGE_MS = 365 * 24 * 60 * 60 * 1000;

/** The stored answer, or null when there is none worth honouring (absent, old, from another version, garbled). */
export function parseChoice(raw: string | null, now: number): Choice | null {
  if (!raw) return null;
  try {
    const v = JSON.parse(raw) as { v?: unknown; choice?: unknown; at?: unknown };
    // A date ahead of the clock is a clock moved back, not a forgery worth
    // guarding against - the entry is the visitor's own - and dropping it would
    // forget a refusal. Only age lapses an answer.
    if (v.v !== CONSENT_VERSION || typeof v.at !== 'number' || now - v.at > CONSENT_MAX_AGE_MS) {
      return null;
    }
    return v.choice === 'granted' || v.choice === 'denied' ? v.choice : null;
  } catch {
    return null;
  }
}

export function serializeChoice(choice: Choice, now: number): string {
  return JSON.stringify({ v: CONSENT_VERSION, choice, at: now });
}

// --- The ad click a visit came from ----------------------------------------------

export const ATTRIBUTION_COOKIE = 'etc_ad';
/** How long the click is kept: the window Google accepts a click back in, and a visitor's week of thinking it over many times. */
export const ATTRIBUTION_DAYS = 90;

/** The address's parameters and the names the api takes them under. */
const ATTRIBUTION_PARAMS = {
  gclid: 'gclid',
  gbraid: 'gbraid',
  wbraid: 'wbraid',
  utm_source: 'utmSource',
  utm_medium: 'utmMedium',
  utm_campaign: 'utmCampaign',
  utm_term: 'utmTerm',
  utm_content: 'utmContent',
} as const;

export type Attribution = Partial<Record<(typeof ATTRIBUTION_PARAMS)[keyof typeof ATTRIBUTION_PARAMS], string>>;

/**
 * What a demo request carries about the click: the click, and the version of the
 * question the visitor allowed - the record the api keeps it under, and without
 * which it keeps none of it (mig 0255).
 */
export type RequestAttribution = Attribution & { consentVersion: number };

/** The bounds the api keeps (the column CHECK, mig 0239). Longer values are dropped there; they are not sent from here. */
const CLICK_ID = /^[A-Za-z0-9._~-]{1,512}$/;
const MAX_WORD = 256;

/**
 * The ad click and campaign words in an address's query, or null when it carries
 * none - a visit no ad brought, which must not overwrite the click that did.
 */
export function attributionFrom(search: string): Attribution | null {
  const params = new URLSearchParams(search);
  const out: Attribution = {};
  for (const [param, field] of Object.entries(ATTRIBUTION_PARAMS)) {
    const value = params.get(param)?.trim();
    if (!value) continue;
    if (field === 'gclid' || field === 'gbraid' || field === 'wbraid') {
      if (CLICK_ID.test(value)) out[field] = value;
    } else if ([...value].length <= MAX_WORD) {
      out[field] = value;
    }
  }
  return Object.keys(out).length ? out : null;
}

/**
 * How many earlier click ids the cookie names beside the click it keeps: a click
 * a newer ad replaced may already have been sent with a request, and a refusal
 * must still be able to take that request's click back.
 */
export const MAX_PRIOR_CLICKS = 2;

/** What the cookie holds: the click kept, and the ids of the clicks it replaced. */
export type KeptClick = Attribution & { prior?: string[] };

/** The cookie's value for an attribution, encoded so any campaign word survives the cookie syntax. */
export function encodeAttribution(a: KeptClick): string {
  return encodeURIComponent(JSON.stringify(a));
}

/** The attribution cookie's parsed value, or null when absent or not ours. */
function readKeptCookie(cookies: string): Record<string, unknown> | null {
  for (const part of cookies.split(';')) {
    const [name, ...rest] = part.trim().split('=');
    if (name !== ATTRIBUTION_COOKIE) continue;
    try {
      const parsed = JSON.parse(decodeURIComponent(rest.join('='))) as unknown;
      return typeof parsed === 'object' && parsed !== null ? (parsed as Record<string, unknown>) : null;
    } catch {
      return null;
    }
  }
  return null;
}

/** Reads the attribution cookie out of a `document.cookie` string; null when absent or not ours. */
export function decodeAttribution(cookies: string): Attribution | null {
  const parsed = readKeptCookie(cookies);
  if (!parsed) return null;
  // Only the fields this site writes, and only strings: a cookie is the
  // visitor's to edit, and what it holds goes into a request body.
  const out: Attribution = {};
  for (const field of Object.values(ATTRIBUTION_PARAMS)) {
    const v = parsed[field];
    if (typeof v === 'string' && v) out[field] = v;
  }
  return attributionFrom(toSearch(out));
}

/** The earlier click ids the cookie names, held to Google's alphabet like any other. */
export function decodePriorClicks(cookies: string): string[] {
  const prior = readKeptCookie(cookies)?.prior;
  if (!Array.isArray(prior)) return [];
  return prior.filter((v): v is string => typeof v === 'string' && CLICK_ID.test(v)).slice(0, MAX_PRIOR_CLICKS);
}

/** Whether an attribution carries one of Google's click ids - the part an ad click is reported back by. */
export function hasClickId(a: Attribution): boolean {
  return Boolean(a.gclid || a.gbraid || a.wbraid);
}

/** Google's click ids among these attributions, each once. */
export function clickIdsOf(...attributions: (Attribution | null)[]): string[] {
  const ids = new Set<string>();
  for (const a of attributions) {
    for (const id of [a?.gclid, a?.gbraid, a?.wbraid]) if (id) ids.add(id);
  }
  return [...ids];
}

/** Where a withdrawn allowance reaches the requests that kept its click. */
export const WITHDRAWALS_PATH = '/api/v1/site-measurement/withdrawals';

/** An attribution written back as the query it came from, so one parser holds both doors to the same shapes. */
function toSearch(a: Attribution): string {
  const params = new URLSearchParams();
  for (const [param, field] of Object.entries(ATTRIBUTION_PARAMS)) {
    const v = a[field];
    if (v) params.set(param, v);
  }
  return params.toString();
}

// --- Google's consent signals -----------------------------------------------------

type ConsentState = Record<'ad_storage' | 'ad_user_data' | 'ad_personalization' | 'analytics_storage', Choice>;

/** All four Consent Mode v2 signals at one value: the bar asks one question, and its answer covers them all. */
export function consentState(choice: Choice): ConsentState {
  return { ad_storage: choice, ad_user_data: choice, ad_personalization: choice, analytics_storage: choice };
}

/**
 * Where the tag starts refused whatever the settings' default: the European
 * Economic Area (the EU's 27, Iceland, Liechtenstein, Norway), the United Kingdom
 * and Switzerland - ISO 3166-1 codes, as Google's `region` takes them.
 */
export const ASK_FIRST_REGIONS = [
  'AT', 'BE', 'BG', 'HR', 'CY', 'CZ', 'DK', 'EE', 'FI', 'FR', 'DE', 'GR', 'HU', 'IE', 'IT', 'LV', 'LT', 'LU',
  'MT', 'NL', 'PL', 'PT', 'RO', 'SK', 'SI', 'ES', 'SE', 'IS', 'LI', 'NO', 'GB', 'CH',
] as const;

type GtagCommand = unknown[];

/**
 * How long the analytics cookie (`_ga`) lives: thirteen months, the ceiling
 * European regulators accept for audience measurement, where Google's own
 * default is two years. The ad click cookies keep Google's ninety days.
 */
export const ANALYTICS_COOKIE_SECONDS = 395 * 24 * 60 * 60;

/**
 * The commands that start the tag, in order. `choice` is the visitor's own, when
 * they have made one; the settings' default stands in until they do, and
 * under `denied` the tag is never started without one (start() checks).
 */
export function startCommands(settings: SiteSettings, choice: Choice | null, opts: { test: boolean; now: Date }): GtagCommand[] {
  const cmds: GtagCommand[] = [];
  if (settings.consentDefault === 'granted') {
    cmds.push(['consent', 'default', { ...consentState('denied'), region: [...ASK_FIRST_REGIONS] }]);
    cmds.push(['consent', 'default', consentState('granted')]);
  } else {
    // The floor any later refusal returns to.
    cmds.push(['consent', 'default', consentState('denied')]);
  }
  if (choice) cmds.push(['consent', 'update', consentState(choice)]);
  // Where storage is refused, the click id is kept out of what the tag sends.
  cmds.push(['set', 'ads_data_redaction', true]);
  cmds.push(['js', opts.now]);
  // `cookie_domain: 'none'`: host-only cookies. Google's default writes them on
  // the registrable domain, which every subdomain - the panels, the api, the
  // schools' sites - would then receive, though the answer was given here.
  if (settings.adsId) cmds.push(['config', settings.adsId, { cookie_domain: 'none' }]);
  if (settings.ga4Id) {
    // A tester's visit (?etc_test=1) is marked, so it lands in DebugView and an
    // "internal traffic" filter keeps it out of the reports.
    cmds.push([
      'config',
      settings.ga4Id,
      {
        cookie_domain: 'none',
        cookie_expires: ANALYTICS_COOKIE_SECONDS,
        ...(opts.test ? { debug_mode: true, traffic_type: 'internal' } : {}),
      },
    ]);
  }
  return cmds;
}

/**
 * The commands that report one recorded demo request: what was asked for and its
 * id, never who asked. Enhanced conversions are off on purpose - the address a
 * visitor types goes to Google for nothing, so the form's promise that it is
 * processed to answer the request and no other way stays whole. Turning them on
 * is a copy change as much as a code one (docs/OPEN_WORK.md).
 */
export function signupCommands(settings: SiteSettings, lead: { id: string; audience: Audience }): GtagCommand[] {
  const cmds: GtagCommand[] = [];
  if (settings.ga4Id) {
    cmds.push(['event', 'sign_up', { send_to: settings.ga4Id, method: 'email', user_role: lead.audience }]);
  }
  // The ad account counts a school and a teacher as two conversions, so bids and
  // reports can tell them apart; a request whose answer has no label is not one.
  const label = lead.audience === 'school' ? settings.adsSchoolLabel : settings.adsTeacherLabel;
  if (settings.adsId && label) {
    cmds.push(['event', 'conversion', { send_to: `${settings.adsId}/${label}`, transaction_id: lead.id }]);
  }
  return cmds;
}

/**
 * The page's own moments the analytics side hears of, beside the recorded request:
 * a press on "Try it free" and where on the page it was, the first touch of the
 * request form with the answer the page has ticked, a press on Telegram or the
 * phone. GA4 only - none of them is an ad conversion - and nothing typed: the
 * form is touched, never read.
 */
export type Moment =
  | { name: 'cta_click'; cta_location: 'hero' | 'header' | 'section' | 'footer'; page_path: string }
  | { name: 'form_start'; user_role: Audience; page_path: string }
  | { name: 'contact_click'; channel: 'telegram' | 'phone'; page_path: string }
  | { name: 'chat_start'; page_path: string };

/** The command that reports one moment, or none without a GA4 id. */
export function momentCommands(settings: SiteSettings, moment: Moment): GtagCommand[] {
  if (!settings.ga4Id) return [];
  const { name, ...params } = moment;
  return [['event', name, { send_to: settings.ga4Id, ...params }]];
}

// --- The page half ------------------------------------------------------------------

type GtagWindow = Window & { dataLayer?: unknown[]; gtag?: (...args: unknown[]) => void };

const TEST_PARAM = 'etc_test';
const TEST_KEY = 'etc-test';
/** Google's own cookie names on this site: the analytics client, the ad click linker. */
const GOOGLE_COOKIE = /^(_ga|_ga_[A-Z0-9]+|_gid|_gcl_[a-z]+|_gac_.+)$/;
/** The local-storage entries Google's tag keeps on this site (the click linker's `_gcl_ls` among them). */
const GOOGLE_STORAGE = /^(_gcl_|_ga)/;
/** A cookie's whole line must stay under what every browser keeps; past it the browser drops the cookie without a word. */
const MAX_COOKIE_VALUE = 3800;

export class Measurement {
  private settings: SiteSettings | null = null;
  /** The answer in force on this page: the stored one, or the one just given where storage refuses to keep it. */
  private choice: Choice | null = null;
  private started = false;
  /** Google's script is on the page (appended once, whatever answers follow). */
  private scriptAdded = false;
  /** Google's script has RUN: before that, nothing queued has been acted on. */
  private loaded = false;
  private readonly reported = new Set<string>();
  /**
   * A request this page recorded before the visitor allowed the measurement:
   * reported, once, if the Allow comes while the page is still open. Held in
   * memory only - another page, a reload or a refusal leaves nothing to send.
   */
  private unreported: { id: string; audience: Audience } | null = null;

  /**
   * `tag: false` is a page that never starts Google's tag (`<body data-no-tag>`):
   * the visitor is still asked, and the answer still kept for the site, but this
   * page loads nothing and reports nothing.
   */
  constructor(
    private readonly doc: Document,
    private readonly win: GtagWindow,
    private readonly opts: { tag: boolean } = { tag: true },
  ) {}

  /** Whether a tag is configured at all - the question the bar asks exists only then. */
  get active(): boolean {
    return this.settings !== null;
  }

  /** The visitor's standing answer, or null when they have not given one that still holds. */
  storedChoice(): Choice | null {
    try {
      return parseChoice(this.win.localStorage.getItem(CONSENT_KEY), Date.now());
    } catch {
      return null; // No storage: no remembered answer, so the bar asks every page.
    }
  }

  /**
   * Takes the site's settings and acts on what the visitor already said.
   * Reports whether the visitor still has to be asked.
   *
   * Measurement switched off (`null`) takes back what an earlier allowance left
   * - the kept click, Google's cookies and storage - and the answer itself,
   * which was given for measurement that has stopped: switched on again, the
   * site asks again. An api that could not be asked (`undefined`) changes
   * nothing: this page measures nothing, and a valid answer outlives a blip.
   */
  begin(settings: SiteSettings | null | undefined): boolean {
    this.settings = settings ?? null;
    if (settings === null) {
      this.forget();
      try {
        this.win.localStorage.removeItem(CONSENT_KEY);
      } catch {
        // No storage: no answer was kept to drop.
      }
      return false;
    }
    if (!settings) return false;
    const choice = this.storedChoice();
    this.choice = choice;
    // A `granted` default stands in only for a visitor who has not answered: one
    // who said no gets no tag at all, in any mode.
    if (choice === 'granted' || (choice === null && settings.consentDefault === 'granted')) this.start(choice);
    // Whoever the tag does not run for holds nothing of it: an answer that has
    // lapsed leaves Google's cookies behind, the analytics one outliving it.
    else this.forget();
    if (choice === 'granted') this.keepClick();
    return choice === null;
  }

  /**
   * Catches up with an answer given on ANOTHER page of the site - another tab,
   * or a later page this one was kept behind by Back (bfcache restores a page
   * without running it again). Reports whether the visitor still has to be
   * asked. A withdrawal elsewhere is a withdrawal here.
   */
  sync(): boolean {
    if (!this.settings) return false;
    const stored = this.storedChoice();
    if (stored && stored !== this.choice) this.apply(stored);
    return this.choice === null;
  }

  /**
   * The visitor answered the bar. A refusal that takes an allowance back also
   * tells the api which clicks this device kept under it - read before they are
   * forgotten - so a request sent under the allowance stops holding its click.
   * Only here, where the visitor answered: another tab that catches up (sync)
   * forgets on its own and sends nothing.
   */
  choose(choice: Choice): void {
    const withdrawn =
      choice === 'denied' && this.choice === 'granted'
        ? [
            ...new Set([
              ...clickIdsOf(attributionFrom(this.win.location.search), decodeAttribution(this.doc.cookie)),
              ...decodePriorClicks(this.doc.cookie),
            ]),
          ]
        : [];
    try {
      this.win.localStorage.setItem(CONSENT_KEY, serializeChoice(choice, Date.now()));
    } catch {
      // Nothing to remember it with: this page honours it, the next one asks again.
    }
    this.apply(choice);
    if (withdrawn.length) this.withdraw(withdrawn);
  }

  /**
   * Hands the api the clicks of a withdrawn allowance. Best-effort and sent to
   * outlive the page (the visitor may leave at once); a refusal that cannot
   * reach the api still stopped everything on the device, and the privacy notice
   * names the address that takes the rest.
   */
  private withdraw(clickIds: string[]): void {
    try {
      void this.win
        .fetch(WITHDRAWALS_PATH, {
          method: 'POST',
          keepalive: true,
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ clickIds }),
        })
        .catch(() => undefined);
    } catch {
      // Nothing to send it with.
    }
  }

  private apply(choice: Choice): void {
    this.choice = choice;
    if (!this.settings) return;
    if (choice === 'granted') {
      if (this.started) {
        this.silenceAnalytics(false);
        this.gtag('consent', 'update', consentState('granted'));
      } else this.start('granted');
      this.keepClick();
      // The request this page recorded before the Allow is reported now, once:
      // the conversion leaves only after the allowance, which is what it needs.
      const late = this.unreported;
      this.unreported = null;
      if (late) this.reportSignup(late);
    } else {
      if (this.started && !this.loaded) {
        // Refused before Google's script has run (an Allow taken back at once,
        // or another tab's answer arriving while it loads): nothing queued has
        // been acted on yet, so the queue is emptied rather than amended - the
        // script then finds no config and sends nothing. An Allow later queues
        // the start again.
        this.win.dataLayer?.splice(0);
        this.started = false;
        this.silenceAnalytics(true);
      } else if (this.started) {
        this.gtag('consent', 'update', consentState('denied'));
        // A refused tag still pings: GA4 sends a cookieless `user_engagement`
        // as the page is left (measured against the real gtag.js). Its own
        // opt-out switch is the one thing that stops it for this page's life.
        this.silenceAnalytics(true);
      }
      this.forget();
    }
  }

  /** GA4's documented kill switch for one stream (`window['ga-disable-G-…']`), read on every hit. */
  private silenceAnalytics(silent: boolean): void {
    const id = this.settings?.ga4Id;
    if (id) (this.win as unknown as Record<string, unknown>)[`ga-disable-${id}`] = silent;
  }

  /**
   * What the demo request carries about the click: only with the visitor's
   * allowance - this page's own address first, the newest click there is, then
   * the cookie the last one was kept in - and with the version of the question
   * that allowance answered. A stored answer is honoured only at the current
   * version (parseChoice), so that is the version allowed.
   */
  attributionForRequest(): RequestAttribution | undefined {
    if (this.choice !== 'granted') return undefined;
    const here = attributionFrom(this.win.location.search);
    const kept = decodeAttribution(this.doc.cookie);
    // An ad click outranks campaign words: a return visit from a newsletter link
    // must not take the click id a paying customer will be reported back by.
    const click = here && (hasClickId(here) || !kept || !hasClickId(kept)) ? here : (kept ?? here);
    return click ? { ...click, consentVersion: CONSENT_VERSION } : undefined;
  }

  /**
   * Reports a recorded demo request - once per id, and only through a started tag.
   *
   * `next.then` is what the page does once the report is on its way: the form
   * whose answer opens the workspace leaves the page, and a hit still queued
   * when the page unloads is a conversion nobody counts. So the last command
   * carries Google's `event_callback`, every one goes as a beacon (which the
   * browser delivers past the navigation), and a timer of `next.within` ms goes
   * on regardless - a tag whose script never loaded, or a blocked request, must
   * not keep a person from their workspace. With nothing to report - no tag, no
   * allowance, a request already reported - `then` runs at once. It runs once.
   */
  reportSignup(lead: { id: string; audience: Audience }, next?: { then: () => void; within: number }): void {
    let done = false;
    const go = () => {
      if (done) return;
      done = true;
      next?.then();
    };
    // A refusal stands even where the tag already runs - an Allow taken back on
    // this page, or a `granted` default the visitor said no to.
    if (!this.settings || !this.started || this.choice === 'denied' || !lead.id || this.reported.has(lead.id)) {
      // Not reported for want of an Allow: kept for one, while this page lives.
      if (this.settings && lead.id && !this.reported.has(lead.id)) this.unreported = lead;
      go();
      return;
    }
    this.reported.add(lead.id);
    const cmds = signupCommands(this.settings, lead);
    if (!next || cmds.length === 0) {
      for (const cmd of cmds) this.gtag(...cmd);
      go();
      return;
    }
    const timer = setTimeout(go, next.within);
    cmds.forEach((cmd, i) => {
      const [kind, name, params] = cmd as [string, string, Record<string, unknown>];
      const last = i === cmds.length - 1;
      this.gtag(kind, name, {
        ...params,
        transport_type: 'beacon',
        ...(last
          ? {
              event_callback: () => {
                clearTimeout(timer);
                go();
              },
            }
          : {}),
      });
    });
  }

  /** Reports one of the page's moments - only through a started tag, never past a refusal. */
  reportMoment(moment: Moment): void {
    if (!this.settings || !this.started || this.choice === 'denied') return;
    for (const cmd of momentCommands(this.settings, moment)) this.gtag(...cmd);
  }

  private start(choice: Choice | null): void {
    const settings = this.settings;
    if (!settings || this.started || !this.opts.tag) return;
    this.started = true;
    this.silenceAnalytics(false);
    this.win.dataLayer = this.win.dataLayer ?? [];
    const layer = this.win.dataLayer;
    // gtag.js reads ARGUMENTS objects off the queue, not arrays - this is the
    // snippet Google publishes, in this file rather than inline, because the
    // site's policy runs no inline script.
    this.win.gtag = function gtag() {
      // eslint-disable-next-line prefer-rest-params
      layer.push(arguments);
    };
    for (const cmd of startCommands(settings, choice, { test: this.isTest(), now: new Date() })) this.gtag(...cmd);
    if (this.scriptAdded) return;
    this.scriptAdded = true;
    const script = this.doc.createElement('script');
    script.async = true;
    script.addEventListener('load', () => {
      this.loaded = true;
    });
    script.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(settings.adsId ?? settings.ga4Id ?? '')}`;
    this.doc.head.append(script);
  }

  private gtag(...args: unknown[]): void {
    this.win.gtag?.(...args);
  }

  /** Keeps the click this visit came from - a newer ad click replaces an older one, a visit no ad brought changes nothing. */
  private keepClick(): void {
    const click = attributionFrom(this.win.location.search);
    if (!click) return;
    // Only a new ad click replaces a kept one; campaign words alone (a
    // newsletter, a partner's post) are kept only where no click is.
    const kept = decodeAttribution(this.doc.cookie);
    if (!hasClickId(click) && kept && hasClickId(kept)) return;
    // The clicks this one replaces stay named, newest first: one may already be
    // with a request, and a refusal takes that request's click back too.
    const fresh = clickIdsOf(click);
    const prior = [...clickIdsOf(kept), ...decodePriorClicks(this.doc.cookie)]
      .filter((id, i, all) => !fresh.includes(id) && all.indexOf(id) === i)
      .slice(0, MAX_PRIOR_CLICKS);
    const secure = this.win.location.protocol === 'https:' ? '; Secure' : '';
    // Campaign words in another script triple when encoded, and a cookie past
    // the browser's limit is dropped silently - the old click would then stand
    // for the new one. So the longest words go first, the click id last.
    const { utmTerm: _t, utmContent: _c, ...shorter } = click;
    const { utmSource: _s, utmMedium: _m, utmCampaign: _p, ...ids } = shorter;
    const value = [click, shorter, ids]
      .map((a) => encodeAttribution(prior.length ? { ...a, prior } : a))
      .find((v) => v.length <= MAX_COOKIE_VALUE);
    if (!value || value === encodeAttribution({})) return;
    this.doc.cookie = `${ATTRIBUTION_COOKIE}=${value}; Max-Age=${ATTRIBUTION_DAYS * 86400}; Path=/; SameSite=Lax${secure}`;
  }

  /** A refusal takes back what an allowance left: the kept click and Google's cookies on this site. */
  private forget(): void {
    const host = this.win.location.hostname;
    const domains = ['', `; Domain=${host}`, `; Domain=.${host.split('.').slice(-2).join('.')}`];
    for (const part of this.doc.cookie.split(';')) {
      const name = part.trim().split('=')[0] ?? '';
      if (name !== ATTRIBUTION_COOKIE && !GOOGLE_COOKIE.test(name)) continue;
      for (const domain of domains) {
        this.doc.cookie = `${name}=; Max-Age=0; Path=/${domain}`;
      }
    }
    try {
      const storage = this.win.localStorage;
      for (const key of Object.keys(storage)) {
        if (GOOGLE_STORAGE.test(key)) storage.removeItem(key);
      }
    } catch {
      // No storage to clear.
    }
  }

  /**
   * The people who run the site checking it: a tester's visit, or an address
   * whose ad click id starts with TEST (the made-up click a check files so the
   * ad network has something to refuse). Said with every request whether or not
   * the visitor allowed the measurement: it says nothing about the visitor, and
   * it is what keeps a check out of the ad network's registrations file.
   */
  testVisit(): boolean {
    return this.isTest() || (attributionFrom(this.win.location.search)?.gclid ?? '').startsWith('TEST');
  }

  /** A tester's visit: `?etc_test=1` once, remembered for the tab. */
  private isTest(): boolean {
    const here = new URLSearchParams(this.win.location.search).get(TEST_PARAM) === '1';
    try {
      if (here) this.win.sessionStorage.setItem(TEST_KEY, '1');
      return here || this.win.sessionStorage.getItem(TEST_KEY) === '1';
    } catch {
      return here;
    }
  }
}

/**
 * Asks the api which tag the site may load: its settings, null when it names
 * none (measurement is off), or undefined when it could not be asked. A site
 * that cannot reach the api measures nothing either way; only a real "off"
 * takes back what an earlier allowance left (Measurement.begin).
 */
export async function fetchSettings(fetcher: typeof fetch = fetch): Promise<SiteSettings | null | undefined> {
  try {
    const res = await fetcher('/api/v1/site-measurement', { headers: { Accept: 'application/json' } });
    if (!res.ok) return undefined;
    return readSettings(await res.json());
  } catch {
    return undefined;
  }
}
